import React, { useRef } from 'react';
import { XReplAIBranding } from '@/app/components/XReplAIBranding';
import { Download, Copy, Check } from 'lucide-react';

export function BrandingDemo() {
  const [copied, setCopied] = React.useState(false);

  const handleDownload = (elementId: string, filename: string) => {
    const element = document.getElementById(elementId);
    if (!element) return;

    // Create canvas from the element
    import('html2canvas').then((html2canvas) => {
      html2canvas.default(element, {
        backgroundColor: null,
        scale: 2, // Higher quality
      }).then((canvas) => {
        // Convert to blob and download
        canvas.toBlob((blob) => {
          if (!blob) return;
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.download = filename;
          link.href = url;
          link.click();
          URL.revokeObjectURL(url);
        });
      });
    });
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText('xReplAI');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-8">
      <div className="max-w-6xl mx-auto space-y-12">
        {/* Header */}
        <div className="text-center space-y-4">
          <h1 className="text-4xl font-bold text-slate-900">
            xReplAI Brand Assets
          </h1>
          <p className="text-lg text-slate-600">
            Your artistic branding with subtle emphasis on 'x' and 'AI'
          </p>
          <div className="flex items-center justify-center gap-2">
            <code className="px-4 py-2 bg-white rounded-lg text-lg font-mono border border-slate-200">
              xReplAI
            </code>
            <button
              onClick={copyToClipboard}
              className="p-2 hover:bg-white rounded-lg transition-colors border border-slate-200"
              title="Copy text"
            >
              {copied ? (
                <Check className="w-5 h-5 text-green-600" />
              ) : (
                <Copy className="w-5 h-5 text-slate-600" />
              )}
            </button>
          </div>
        </div>

        {/* Hero Size - Dark */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-slate-900">Hero - Dark (Recommended)</h2>
            <button
              onClick={() => handleDownload('branding-hero-dark', 'xreplai-hero-dark.png')}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Download className="w-4 h-4" />
              Download PNG
            </button>
          </div>
          <div id="branding-hero-dark">
            <XReplAIBranding variant="dark" size="xl" showGlow={true} />
          </div>
          <p className="text-sm text-slate-600">
            Perfect for: Website headers, social media covers, presentations
          </p>
        </div>

        {/* Large Size - Light */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-slate-900">Large - Light</h2>
            <button
              onClick={() => handleDownload('branding-large-light', 'xreplai-large-light.png')}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Download className="w-4 h-4" />
              Download PNG
            </button>
          </div>
          <div id="branding-large-light">
            <XReplAIBranding variant="light" size="lg" showGlow={true} />
          </div>
          <p className="text-sm text-slate-600">
            Perfect for: Light backgrounds, email signatures, business cards
          </p>
        </div>

        {/* Medium Size - Dark */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-slate-900">Medium - Dark</h2>
            <button
              onClick={() => handleDownload('branding-medium-dark', 'xreplai-medium-dark.png')}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Download className="w-4 h-4" />
              Download PNG
            </button>
          </div>
          <div id="branding-medium-dark">
            <XReplAIBranding variant="dark" size="md" showGlow={true} />
          </div>
          <p className="text-sm text-slate-600">
            Perfect for: Blog posts, thumbnails, social media posts
          </p>
        </div>

        {/* Compact - No Glow */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-slate-900">Compact - Clean</h2>
            <button
              onClick={() => handleDownload('branding-compact', 'xreplai-compact.png')}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Download className="w-4 h-4" />
              Download PNG
            </button>
          </div>
          <div id="branding-compact">
            <XReplAIBranding variant="dark" size="md" showGlow={false} />
          </div>
          <p className="text-sm text-slate-600">
            Perfect for: Icons, favicons, small spaces, minimalist designs
          </p>
        </div>

        {/* Side by Side Comparison */}
        <div className="space-y-4">
          <h2 className="text-2xl font-bold text-slate-900">All Variants</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <XReplAIBranding variant="dark" size="lg" showGlow={true} />
            </div>
            <div>
              <XReplAIBranding variant="light" size="lg" showGlow={true} />
            </div>
          </div>
        </div>

        {/* Usage Guide */}
        <div className="bg-white rounded-2xl p-8 border border-slate-200 space-y-6">
          <h2 className="text-2xl font-bold text-slate-900">Design Notes</h2>
          
          <div className="space-y-4">
            <div>
              <h3 className="font-semibold text-slate-900 mb-2">Subtle xAI Hint</h3>
              <ul className="list-disc list-inside text-slate-600 space-y-1">
                <li><strong>'x'</strong> - Emphasized in bright blue with glow effect</li>
                <li><strong>'Repl'</strong> - Neutral weight to balance the design</li>
                <li><strong>'AI'</strong> - Emphasized in bright blue with glow effect</li>
                <li>Together: x...AI creates a subliminal reference to xAI</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-slate-900 mb-2">Color Psychology</h3>
              <ul className="list-disc list-inside text-slate-600 space-y-1">
                <li><strong>Deep Indigo (#1E3A8A)</strong> - Professional, trustworthy, tech-forward</li>
                <li><strong>Light Blue (#60A5FA)</strong> - Innovation, intelligence, clarity</li>
                <li>Gradient backgrounds - Premium, modern aesthetic</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-slate-900 mb-2">Best Practices</h3>
              <ul className="list-disc list-inside text-slate-600 space-y-1">
                <li>Use dark variant for primary branding (hero, headers)</li>
                <li>Use light variant for light backgrounds or printables</li>
                <li>Maintain aspect ratio when resizing</li>
                <li>Ensure sufficient contrast with surrounding content</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Installation Note */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
          <p className="text-sm text-slate-700">
            <strong>Note:</strong> To download images, you'll need to install the <code className="bg-white px-2 py-1 rounded">html2canvas</code> package. 
            Run: <code className="bg-white px-2 py-1 rounded">npm install html2canvas</code>
          </p>
        </div>
      </div>
    </div>
  );
}
